import { AiContext, CanonStatus, Capability, DiscoveryState, Library, PlayState, } from '../domain/enums.js';
import { z } from '../zod.js';
import { Fid, IsoTimestamp, Uuid } from './common.js';
/**
 * The document DTOs of the skeleton and editor routes (1.0b deliverable 7), frozen at
 * `editor-alpha`. Field names are the camelCase of baseline section 3's `document` columns as
 * amended by A2.1 section 4; derived columns are null until their deriver has run. In a response
 * an absent value is null; in a request an omitted field is optional.
 *
 * The save request (`PutBodyRequestV1`) is what the editor's step buffer keeps in the GM's
 * browser: it never loses or renames a field (1.0b rule 7).
 */
/** Moved to `src/domain/enums.ts` (1.0b deliverable 1); kept here so old imports resolve. */
export { CanonStatus };
/** A document type key, such as `quest` or a user-defined `deity`. */
export const TypeKey = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/);
/** A stored title: at least one non-blank character. */
export const DocTitle = z.string().min(1).max(500).regex(/\S/);
/** Any JSON object. The save pipeline checks a body against the editor schema (422 on failure). */
export const JsonObject = z.record(z.string(), z.unknown());
/** The editor JSON of a stored body: a ProseMirror `doc` node, checked by doc-model on save. */
export const DocBodyV1 = z.looseObject({ type: z.literal('doc') });
/** A positive version counter (`body_version`, `meta_version`, a schema version). */
const Version = z.number().int().min(1);
/** One alias of a document (`document.aliases`): a secret alias is never shown at the table. */
export const DocAliasV1 = z.strictObject({
    name: z.string().min(1).max(500).regex(/\S/),
    caseSensitive: z.boolean(),
    secret: z.boolean(),
});
/**
 * A value in `document.props`, keyed by `field_def.id`: a scalar, or a list of strings for a
 * `multi_select` field. Links, placements and states are never props (baseline section 3).
 */
export const PropValueV1 = z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(z.string()),
]);
export const DocPropsV1 = z.record(Uuid, PropValueV1);
const documentMetaShape = {
    id: Uuid,
    fid: Fid,
    typeId: Uuid,
    typeKey: TypeKey,
    library: Library,
    /** "Untitled" while `titlePending` is true (baseline section 3, "Title"). */
    title: DocTitle,
    titlePending: z.boolean(),
    icon: z.string().nullable(),
    coverAssetId: Uuid.nullable(),
    summary: z.string().nullable(),
    aliases: z.array(DocAliasV1),
    bodySchemaVersion: Version,
    canonStatus: CanonStatus,
    aiContext: AiContext,
    autolink: z.boolean(),
    isStub: z.boolean(),
    mergedIntoId: Uuid.nullable(),
    bodyVersion: Version,
    metaVersion: Version,
    /** Derived (1.A's body deriver); null until it has run. */
    wordCount: z.number().int().nonnegative().nullable(),
    /** Derived (1.M-1); null until it has run. */
    playState: PlayState.nullable(),
    /** Derived (1.M-1); null until it has run, and on a reference document. */
    discovery: DiscoveryState.nullable(),
    deletedAt: IsoTimestamp.nullable(),
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
};
/** A whole document: what `docs.get` answers and the editor's clean reload reads. */
export const DocumentV1 = z.strictObject({
    ...documentMetaShape,
    body: DocBodyV1,
    props: DocPropsV1,
});
/**
 * A document without its body and props, with its type's capabilities and its primary parent
 * (`homeId`, null until 1.C's primary links exist).
 */
export const DocMetaV1 = z.strictObject({
    ...documentMetaShape,
    capabilities: z.array(Capability),
    homeId: Uuid.nullable(),
});
/** The metadata of a document: everything but its body and props, with type facts added. */
export function docMetaFrom(doc, extra) {
    const meta = Object.fromEntries(Object.entries(doc).filter(([key]) => key !== 'body' && key !== 'props'));
    return { ...meta, capabilities: [...extra.capabilities], homeId: extra.homeId };
}
export const DocIdParams = z.strictObject({ id: Uuid });
/**
 * `docs.create`. Without a title the server stores "Untitled" with `titlePending: true`; without
 * a body, one empty paragraph. A reference type key answers 403 `reference_read_only`; an unknown
 * or archived one 422 `invalid_body`.
 */
export const CreateDocRequestV1 = z.strictObject({
    typeKey: TypeKey,
    title: DocTitle.optional(),
    body: JsonObject.optional(),
    bodySchemaVersion: Version.optional(),
    isStub: z.boolean().optional(),
});
/**
 * A block cut from another document. `fromDocId` is an owner pointer of `docs.putBody`, so a
 * block can never be moved out of a reference document. The token is minted and checked by 1.A.
 */
export const BlockMoveV1 = z.strictObject({
    id: Uuid,
    fromDocId: Uuid,
    moveToken: z.string().min(1).max(1000),
});
/**
 * A save. `bodySchemaVersion` is optional: omitted, the server reads the body at its current
 * schema version; the editor always sends it, and a version the server cannot read answers 422
 * `unsupported_schema_version`. `proposalId` stays unused until Phase 3.
 */
export const PutBodyRequestV1 = z.strictObject({
    baseBodyVersion: Version,
    clientSeq: z.number().int().nonnegative(),
    body: JsonObject,
    moves: z.array(BlockMoveV1),
    bodySchemaVersion: Version.optional(),
    proposalId: Uuid.optional(),
});
/** One id the server changed (doc-model's `IdPatch`): `path` is child indexes from the root. */
export const IdPatchV1 = z.strictObject({
    path: z.array(z.number().int().nonnegative()),
    attr: z.enum(['id', 'secretId']),
    from: z.string().nullable(),
    to: Uuid,
});
/** Something the save noticed but did not refuse, such as a re-minted duplicate id. */
export const SaveWarningV1 = z.strictObject({
    code: z.string().regex(/^[a-z][a-z0-9_]*$/),
    path: z.array(z.number().int().nonnegative()).optional(),
    message: z.string(),
});
export const PutBodyResponseV1 = z.strictObject({
    bodyVersion: Version,
    clientSeq: z.number().int().nonnegative(),
    patches: z.array(IdPatchV1),
    warnings: z.array(SaveWarningV1),
});
/**
 * What `docs.create` answers (CS r1, DECISIONS 60 item 5): the document, and, when the server
 * re-minted an id the sent body held (a duplicate, or one another document holds), the same
 * `patches` and `warnings` a save answers, so the editor that created the page can follow the
 * new ids. Both are optional and additive: an answer without them is a `DocumentV1`, and a tab
 * running an older bundle ignores them (success answers are not validated in production).
 */
export const CreatedDocumentV1 = DocumentV1.extend({
    patches: z.array(IdPatchV1).optional(),
    warnings: z.array(SaveWarningV1).optional(),
});
/**
 * `details` of a 409 `version_conflict` (baseline section 4, step 8): the versions the server
 * holds now and the revision that stores the client's body (`conflict`), or null when no
 * revision was written (a metadata conflict, or before 1.A stores conflict revisions). The body
 * itself is fetched with `docs.get`.
 */
export const VersionConflictDetailsV1 = z.strictObject({
    current: z.strictObject({ bodyVersion: Version, metaVersion: Version }),
    conflictRevNo: z.number().int().min(1).nullable(),
});
/** Every metadata write may name the version it was based on; stale answers 409. */
const metaBase = { baseMetaVersion: Version.optional() };
/**
 * One metadata field per request; it bumps only `metaVersion`. With a stale `baseMetaVersion`
 * the answer is 409 `version_conflict` (`conflictRevNo: null`); without it the write is
 * last-writer-wins (baseline section 3, "Versions"). A title blank after trimming, an unknown
 * cover asset and an unknown or inapplicable field answer 422 `invalid_body`.
 */
export const PatchMetaRequestV1 = z.discriminatedUnion('field', [
    z.strictObject({ field: z.literal('title'), value: z.string().max(500), ...metaBase }),
    z.strictObject({ field: z.literal('icon'), value: z.string().max(200).nullable(), ...metaBase }),
    z.strictObject({ field: z.literal('coverAssetId'), value: Uuid.nullable(), ...metaBase }),
    z.strictObject({
        field: z.literal('summary'),
        value: z.string().max(10_000).nullable(),
        ...metaBase,
    }),
    z.strictObject({ field: z.literal('aliases'), value: z.array(DocAliasV1), ...metaBase }),
    z.strictObject({
        field: z.literal('canonStatus'),
        value: CanonStatus.exclude(['reference']),
        ...metaBase,
    }),
    z.strictObject({ field: z.literal('aiContext'), value: AiContext.exclude(['n_a']), ...metaBase }),
    z.strictObject({ field: z.literal('autolink'), value: z.boolean(), ...metaBase }),
    z.strictObject({
        field: z.literal('prop'),
        fieldDefId: Uuid,
        value: PropValueV1,
        ...metaBase,
    }),
]);
export const PatchMetaResponseV1 = z.strictObject({ metaVersion: Version });
/** How `docs.titles` found an id. */
export const DOC_TITLE_STATES = ['live', 'trashed', 'merged', 'missing'];
export const DocTitleState = z.enum(DOC_TITLE_STATES);
const titleFound = {
    id: Uuid,
    fid: Fid,
    title: DocTitle,
    titlePending: z.boolean(),
    typeKey: TypeKey,
    library: Library,
    isStub: z.boolean(),
};
/**
 * One id resolved by `docs.titles`: a live or trashed document, a merged one (with its
 * survivor), or a missing id, whose other fields are null.
 */
export const DocTitleV1 = z.discriminatedUnion('state', [
    z.strictObject({
        ...titleFound,
        state: z.enum(['live', 'trashed']),
        mergedIntoId: z.null(),
    }),
    z.strictObject({ ...titleFound, state: z.literal('merged'), mergedIntoId: Uuid }),
    z.strictObject({
        id: Uuid,
        fid: z.null(),
        title: z.null(),
        titlePending: z.null(),
        typeKey: z.null(),
        library: z.null(),
        isStub: z.null(),
        state: z.literal('missing'),
        mergedIntoId: z.null(),
    }),
]);
export const DocTitlesRequestV1 = z.strictObject({ ids: z.array(Uuid).min(1).max(500) });
export const DocTitlesResponseV1 = z.strictObject({ titles: z.array(DocTitleV1) });
/**
 * Where `docs.moveBlocks` sends blocks: an existing document (`docId`), or a new child page of
 * the source, titled and typed as given (the source's type and "Untitled" when omitted).
 */
export const MoveDestinationV1 = z.union([
    z.strictObject({ blockIds: z.array(Uuid).min(1), docId: Uuid }),
    z.strictObject({
        blockIds: z.array(Uuid).min(1),
        title: DocTitle.optional(),
        typeKey: TypeKey.optional(),
    }),
]);
/** Turn into page, Move to and Split at heading (checklist item 4b), keeping every id. */
export const MoveBlocksRequestV1 = z.strictObject({
    baseBodyVersion: Version,
    destinations: z.array(MoveDestinationV1).min(1),
});
export const MoveBlocksResponseV1 = z.strictObject({
    source: PutBodyResponseV1,
    created: z.array(DocMetaV1),
});
//# sourceMappingURL=documents.js.map